/**
 * sqlite3mt Public Manifest v2 — §104 wire types, canonicalization, and operations
 *
 * Defines the public manifest tree, root descriptor, commit payload, and
 * page object identity for sqlite3mt-public-root-v2.
 *
 * Per §12: sqlite3mt-root-v1 and old encrypted state blob concepts are removed.
 * No backward compatibility with pre-v2 formats.
 *
 * Per §7: Fixed-depth sparse radix tree with fanout=256, depth=4.
 * Per §5.3: rootId = SHA-256(canonicalUtf8(rootDescriptor)).
 * Per §5.1: pageObjectId = HMAC-SHA-256(K_sqlite3mt_pages, pageContentId).
 */

import { canonicalize } from '../codec/state'
import type { Root, RootId } from './types'

// ── Constants (§7.1) ──────────────────────────────────────────────────

/** Fixed fanout for the manifest radix tree. */
export const MANIFEST_V2_FANOUT = 256

/** Fixed depth for the manifest radix tree (levels from root to leaf, inclusive). */
export const MANIFEST_V2_DEPTH = 4

// ── Wire types: Manifest nodes (§7.2) ──────────────────────────────────

/**
 * Internal manifest node — points to child manifest nodes.
 *
 * Each entry in `children` is either a manifestObjectId of a child node,
 * or null for an empty subtree (§7.3 canonicalization).
 */
export interface Sqlite3mtManifestInternalNode {
    readonly type: 'sqlite3mt-manifest-v2'
    readonly kind: 'internal'
    /** Exactly MANIFEST_V2_FANOUT entries. */
    readonly children: ReadonlyArray<string | null>
}

/**
 * Leaf manifest node — points to encrypted page objects.
 *
 * Each entry in `pages` is either an opaque pageObjectId, or null
 * for a sparse/empty slot.
 */
export interface Sqlite3mtManifestLeafNode {
    readonly type: 'sqlite3mt-manifest-v2'
    readonly kind: 'leaf'
    /** Exactly MANIFEST_V2_FANOUT entries. */
    readonly pages: ReadonlyArray<string | null>
}

/** Union of the two manifest node kinds. */
export type Sqlite3mtManifestV2Node =
    | Sqlite3mtManifestInternalNode
    | Sqlite3mtManifestLeafNode

// ── Wire types: Root descriptor (§5.3, §6) ──────────────────────────────

/**
 * Canonical root descriptor for sqlite3mt-public-root-v2.
 *
 * This is the exact object that gets canonicalized and hashed to produce rootId.
 * pageCount and pageSize MUST be unsigned decimal strings (§5.3 normative).
 * version MUST NOT appear here (§5.3: "version MUST inte ingå i rootId").
 */
export interface PublicRootDescriptor {
    readonly kind: 'sqlite3mt-public-root-v2'
    readonly manifestRootId: string | null
    /** Unsigned decimal string. */
    readonly pageCount: string
    /** Unsigned decimal string. */
    readonly pageSize: string
    readonly parentPublicRootId: string | null
}

/**
 * Full commit payload for sqlite3mt-public-root-v2 (§6).
 *
 * This is the `payload` object inside the signed CommitRecord.
 * All numeric fields are decimal strings because the commit JSON validator
 * does not allow number tokens in payload beyond the top-level `v` field.
 */
export interface Sqlite3mtPublicRootV2Payload {
    readonly kind: 'sqlite3mt-public-root-v2'
    readonly rootId: string
    readonly manifestRootId: string | null
    /** Unsigned decimal string. */
    readonly pageCount: string
    /** Unsigned decimal string. */
    readonly pageSize: string
    readonly parentPublicRootId: string | null
    /** Unsigned decimal string — strictly monotonic per lane. */
    readonly version: string
}

/**
 * Decoded sqlite3mt-public-root-v2 payload.
 *
 * The runtime uses Root.meta.manifestRootId internally, mapping directly to
 * the wire-level manifestRootId field. Version is kept separate as bigint.
 */
export interface DecodedPublicRootV2 {
    readonly root: Root
    readonly version: bigint
    readonly pageSize: number
}

// ── Canonical encoding and hashing ────────────────────────────────────

/**
 * Encode a manifest node as canonical JSON UTF-8 bytes.
 *
 * Uses the shared canonicalize() function which produces sorted-key JSON,
 * ensuring deterministic cross-platform encoding.
 */
export function encodeManifestNode(node: Sqlite3mtManifestV2Node): Uint8Array {
    return new TextEncoder().encode(canonicalize(node))
}

/**
 * Encode a root descriptor as canonical JSON UTF-8 bytes.
 */
export function encodePublicRootDescriptor(descriptor: PublicRootDescriptor): Uint8Array {
    return new TextEncoder().encode(canonicalize(descriptor))
}

/**
 * Compute manifestObjectId from a manifest node (§5.2).
 *
 * manifestObjectId = SHA-256(canonicalUtf8(manifestNode))
 */
export async function computeManifestObjectId(
    node: Sqlite3mtManifestV2Node,
    sha256: (data: Uint8Array) => Promise<string>,
): Promise<string> {
    const canonical = encodeManifestNode(node)
    return sha256(canonical)
}

/**
 * Compute rootId from a canonical root descriptor (§5.3).
 *
 * rootId = SHA-256(canonicalUtf8({kind, manifestRootId, pageCount, pageSize, parentPublicRootId}))
 *
 * pageCount and pageSize are decimal strings in the canonical form,
 * never numeric types. version is NOT included (§5.3 normative).
 */
export async function computePublicRootId(
    descriptor: PublicRootDescriptor,
    sha256: (data: Uint8Array) => Promise<string>,
): Promise<string> {
    const canonical = encodePublicRootDescriptor(descriptor)
    return sha256(canonical)
}

// ── Page object identity (§5.1) ────────────────────────────────────────

/**
 * Compute pageContentId from plaintext page bytes (§5.1).
 *
 * pageContentId = SHA-256(pagePlaintext)
 *
 * This is client-internal — never exposed to the server.
 */
export async function computePageContentId(
    pagePlaintext: Uint8Array,
    sha256: (data: Uint8Array) => Promise<string>,
): Promise<string> {
    return sha256(pagePlaintext)
}

/**
 * Compute opaque pageObjectId from pageContentId using workspace-scoped HMAC key (§5.1).
 *
 * pageObjectId = HMAC-SHA-256(K_sqlite3mt_pages, pageContentId)
 *
 * The HMAC input is the raw bytes of the pageContentId hex digest.
 * K_sqlite3mt_pages MUST be blob-scoped, not lane-scoped (§5.1 normative).
 */
export async function computePageObjectId(
    pageContentIdHex: string,
    hmacKey: Uint8Array,
    hmacSha256: (key: Uint8Array, data: Uint8Array) => Promise<string>,
): Promise<string> {
    const data = hexToUint8Array(pageContentIdHex)
    return hmacSha256(hmacKey, data)
}

// ── Manifest node builders ────────────────────────────────────────────

/**
 * Create an internal manifest node with exactly MANIFEST_V2_FANOUT children slots.
 *
 * Input shorter than FANOUT is padded with null; longer is truncated.
 */
export function createInternalNode(
    children: ReadonlyArray<string | null>,
): Sqlite3mtManifestInternalNode {
    const padded = padArray(children, MANIFEST_V2_FANOUT, null)
    return Object.freeze({
        type: 'sqlite3mt-manifest-v2',
        kind: 'internal',
        children: Object.freeze(padded),
    })
}

/**
 * Create a leaf manifest node with exactly MANIFEST_V2_FANOUT page slots.
 *
 * Input shorter than FANOUT is padded with null; longer is truncated.
 */
export function createLeafNode(
    pages: ReadonlyArray<string | null>,
): Sqlite3mtManifestLeafNode {
    const padded = padArray(pages, MANIFEST_V2_FANOUT, null)
    return Object.freeze({
        type: 'sqlite3mt-manifest-v2',
        kind: 'leaf',
        pages: Object.freeze(padded),
    })
}

// ── Root descriptor builder ────────────────────────────────────────────

/**
 * Create a public root descriptor with numeric→decimal-string conversion (§5.3).
 */
export function createPublicRootDescriptor(args: {
    manifestRootId: string | null
    pageCount: number
    pageSize: number
    parentPublicRootId: string | null
}): PublicRootDescriptor {
    return {
        kind: 'sqlite3mt-public-root-v2',
        manifestRootId: args.manifestRootId,
        pageCount: encodeUnsignedDecimal(args.pageCount, 'pageCount'),
        pageSize: encodeUnsignedDecimal(args.pageSize, 'pageSize'),
        parentPublicRootId: args.parentPublicRootId,
    }
}

/**
 * Create the full commit payload for sqlite3mt-public-root-v2 (§6).
 *
 * rootId is computed from the canonical descriptor.
 * version is encoded as decimal string.
 */
export async function createPublicRootV2Payload(args: {
    manifestRootId: string | null
    pageCount: number
    pageSize: number
    parentPublicRootId: string | null
    version: bigint | number | string
    sha256: (data: Uint8Array) => Promise<string>
}): Promise<Sqlite3mtPublicRootV2Payload> {
    const descriptor = createPublicRootDescriptor(args)
    const rootId = await computePublicRootId(descriptor, args.sha256)

    return {
        kind: 'sqlite3mt-public-root-v2',
        rootId,
        manifestRootId: args.manifestRootId,
        pageCount: descriptor.pageCount,
        pageSize: descriptor.pageSize,
        parentPublicRootId: args.parentPublicRootId,
        version: encodeVersion(args.version),
    }
}

/**
 * Encode a runtime Root as a sqlite3mt-public-root-v2 payload.
 *
 * This enforces the §6 payload shape while keeping Root as the internal SDK
 * carrier type during the cutover.
 */
export async function encodePublicRootV2PayloadFromRoot(args: {
    root: Root
    pageSize: number
    version: bigint | number | string
    sha256: (data: Uint8Array) => Promise<string>
}): Promise<Sqlite3mtPublicRootV2Payload> {
    const payload = await createPublicRootV2Payload({
        manifestRootId: args.root.meta.pageCount === 0 ? null : args.root.meta.manifestRootId,
        pageCount: args.root.meta.pageCount,
        pageSize: args.pageSize,
        parentPublicRootId: args.root.meta.parentPublicRootId as string | null,
        version: args.version,
        sha256: args.sha256,
    })

    if (payload.rootId !== args.root.rootId) {
        throw new Error(
            `sqlite3mt public root payload rootId mismatch: runtime=${args.root.rootId} payload=${payload.rootId}`,
        )
    }

    return payload
}

/**
 * Decode and validate a sqlite3mt-public-root-v2 payload into the runtime Root shape.
 */
export async function decodePublicRootV2Payload(
    payload: Record<string, unknown>,
    args: {
        sha256: (data: Uint8Array) => Promise<string>
        expectedPageSize?: number
    },
): Promise<DecodedPublicRootV2> {
    const validationError = validatePublicRootV2Payload(payload)
    if (validationError != null) {
        throw new Error(`invalid sqlite3mt-public-root-v2 payload: ${validationError}`)
    }

    const typedPayload = payload as unknown as Sqlite3mtPublicRootV2Payload
    const descriptor: PublicRootDescriptor = {
        kind: typedPayload.kind,
        manifestRootId: typedPayload.manifestRootId,
        pageCount: typedPayload.pageCount,
        pageSize: typedPayload.pageSize,
        parentPublicRootId: typedPayload.parentPublicRootId,
    }
    const computedRootId = await computePublicRootId(descriptor, args.sha256)
    if (computedRootId !== typedPayload.rootId) {
        throw new Error(
            `sqlite3mt public root payload rootId mismatch: expected ${computedRootId}, got ${typedPayload.rootId}`,
        )
    }

    const pageCount = parseUnsignedDecimalToSafeInteger(typedPayload.pageCount, 'pageCount')
    const pageSize = parseUnsignedDecimalToSafeInteger(typedPayload.pageSize, 'pageSize')
    if (args.expectedPageSize != null && pageSize !== args.expectedPageSize) {
        throw new Error(
            `sqlite3mt public root payload pageSize mismatch: expected ${args.expectedPageSize}, got ${pageSize}`,
        )
    }

    return {
        root: {
            rootId: typedPayload.rootId as RootId,
            meta: {
                manifestRootId: typedPayload.manifestRootId ?? '',
                pageCount,
                parentPublicRootId: typedPayload.parentPublicRootId as RootId | null,
            },
        },
        version: BigInt(typedPayload.version),
        pageSize,
    }
}

/**
 * Construct the local empty-root bootstrap value for a sqlite3mt lane.
 *
 * This is not a remote head object; it is the local "no remote head yet"
 * sentinel used before the first successful publish.
 */
export async function createEmptyPublicRoot(args: {
    pageSize: number
    sha256: (data: Uint8Array) => Promise<string>
}): Promise<Root> {
    const descriptor = createPublicRootDescriptor({
        manifestRootId: null,
        pageCount: 0,
        pageSize: args.pageSize,
        parentPublicRootId: null,
    })

    return {
        rootId: await computePublicRootId(descriptor, args.sha256) as RootId,
        meta: {
            manifestRootId: '',
            pageCount: 0,
            parentPublicRootId: null,
        },
    }
}

// ── Tree materialization (build manifest from page slots) ──────────────

/**
 * Result of materializing a public manifest tree.
 */
export interface MaterializedManifestTree {
    /** manifestObjectId of the root node. null when pageCount is 0. */
    readonly manifestRootId: string | null
    /** All manifest nodes in the tree, keyed by manifestObjectId. */
    readonly nodes: ReadonlyMap<string, Sqlite3mtManifestV2Node>
    /** Canonical bytes for each node, keyed by manifestObjectId. */
    readonly nodeBytes: ReadonlyMap<string, Uint8Array>
}

/**
 * Build a complete public manifest tree from a sparse page map (§7, §9).
 *
 * Takes a map of 0-based page indices → pageObjectId values and a total
 * page count. Returns the full manifest tree with all nodes canonicalized
 * and hashed.
 *
 * Tree structure (depth=4, fanout=256):
 *   Level 0 (root internal): slot = (pageIndex >> 24) & 0xFF
 *   Level 1 (internal):      slot = (pageIndex >> 16) & 0xFF
 *   Level 2 (internal):      slot = (pageIndex >> 8) & 0xFF
 *   Level 3 (leaf):           slot = pageIndex & 0xFF
 *
 * Per §7.3: empty subtrees are null in parent; slots beyond pageCount are null.
 */
export async function materializePublicManifestTree(
    pageSlots: ReadonlyMap<number, string>,
    pageCount: number,
    sha256: (data: Uint8Array) => Promise<string>,
): Promise<MaterializedManifestTree> {
    if (pageCount === 0) {
        return { manifestRootId: null, nodes: new Map(), nodeBytes: new Map() }
    }

    const nodes = new Map<string, Sqlite3mtManifestV2Node>()
    const nodeBytes = new Map<string, Uint8Array>()

    // ── Build leaves (level 3) ──
    // Group pages by their 3-level prefix: (b3, b2, b1)
    const leafGroups = new Map<string, Map<number, string>>()
    for (const [pageIndex, pageObjectId] of pageSlots) {
        if (pageIndex < 0 || pageIndex >= pageCount) continue
        const leafKey = manifestPathPrefix(pageIndex, 3)
        let group = leafGroups.get(leafKey)
        if (!group) {
            group = new Map()
            leafGroups.set(leafKey, group)
        }
        group.set(pageIndex & 0xFF, pageObjectId)
    }

    const leafHashes = new Map<string, string>() // leafKey → manifestObjectId
    for (const [leafKey, pages] of leafGroups) {
        const pageArray: Array<string | null> = new Array(MANIFEST_V2_FANOUT).fill(null)
        for (const [slot, pageObjectId] of pages) {
            pageArray[slot] = pageObjectId
        }
        const node = createLeafNode(pageArray)
        const { id, bytes } = await sealNode(node, sha256)
        nodes.set(id, node)
        nodeBytes.set(id, bytes)
        leafHashes.set(leafKey, id)
    }

    // ── Build internal level 2 ──
    const i2Hashes = await buildInternalLevel(leafHashes, 2, nodes, nodeBytes, sha256)

    // ── Build internal level 1 ──
    const i1Hashes = await buildInternalLevel(i2Hashes, 1, nodes, nodeBytes, sha256)

    // ── Build root internal (level 0) ──
    const rootChildren: Array<string | null> = new Array(MANIFEST_V2_FANOUT).fill(null)
    for (const [key, hash] of i1Hashes) {
        const slot = Number(key)
        rootChildren[slot] = hash
    }
    const rootNode = createInternalNode(rootChildren)
    const { id: manifestRootId, bytes: rootBytes } = await sealNode(rootNode, sha256)
    nodes.set(manifestRootId, rootNode)
    nodeBytes.set(manifestRootId, rootBytes)

    return { manifestRootId, nodes, nodeBytes }
}

/**
 * Incrementally materialize a new manifest tree from a base tree + page overlay (§9).
 *
 * Only the manifest path from affected leaves to root is rewritten.
 * All unchanged subtrees are reused (same manifestObjectId → same node).
 *
 * This is the core write-path operation: O(changed × depth).
 *
 * @param baseManifestRootId - manifestObjectId of the base tree. null for first publish.
 * @param pageOverlay - map of 0-based page index → new pageObjectId
 * @param pageCount - new total page count
 * @param basePageCount - old total page count (needed for truncation handling).
 *   If omitted, truncation-only changes are not detected (caller must put null
 *   entries in overlay for truncated slots).
 * @param loadNode - function to load a manifest node by manifestObjectId
 * @param sha256 - SHA-256 function
 */
export async function materializePublicManifestIncremental(
    baseManifestRootId: string | null,
    pageOverlay: ReadonlyMap<number, string>,
    pageCount: number,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
    sha256: (data: Uint8Array) => Promise<string>,
    basePageCount?: number,
): Promise<MaterializedManifestTree> {
    if (pageCount === 0) {
        return { manifestRootId: null, nodes: new Map(), nodeBytes: new Map() }
    }

    const nodes = new Map<string, Sqlite3mtManifestV2Node>()
    const nodeBytes = new Map<string, Uint8Array>()

    // Collect all affected paths (one per leaf group that has changes)
    const affectedPaths = new Set<string>()
    for (const pageIndex of pageOverlay.keys()) {
        if (pageIndex >= 0 && pageIndex < pageCount) {
            affectedPaths.add(manifestPathPrefix(pageIndex, 3))
        }
    }

    // §9.1: If pageCount decreased, find leaf groups that may need truncation
    if (basePageCount != null && basePageCount > pageCount && baseManifestRootId != null) {
        // Add leaf groups for the truncated range [pageCount, basePageCount)
        for (let idx = pageCount; idx < basePageCount; idx++) {
            affectedPaths.add(manifestPathPrefix(idx, 3))
        }
    }

    // For each affected leaf path, build new leaf by merging base + overlay
    const newLeafHashes = new Map<string, string>() // leafKey → manifestObjectId
    for (const leafKey of affectedPaths) {
        const pages: Array<string | null> = new Array(MANIFEST_V2_FANOUT).fill(null)

        // Load base leaf if it exists
        if (baseManifestRootId != null) {
            const baseLeafId = await resolveChildAtPath(baseManifestRootId, leafKey, loadNode)
            if (baseLeafId != null) {
                const baseLeaf = await loadNode(baseLeafId)
                if (baseLeaf?.kind === 'leaf') {
                    for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
                        pages[i] = baseLeaf.pages[i]
                    }
                }
            }
        }

        // Apply overlay changes
        for (const [pageIndex, pageObjectId] of pageOverlay) {
            if (manifestPathPrefix(pageIndex, 3) !== leafKey) continue
            const slot = pageIndex & 0xFF
            if (pageIndex < pageCount) {
                pages[slot] = pageObjectId
            }
        }

        // Clear slots beyond pageCount that fall in this leaf
        const [b3, b2, b1] = leafKey.split(':').map(Number)
        const leafBaseIndex = (b3 << 24) | (b2 << 16) | (b1 << 8)
        for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
            if (leafBaseIndex + i >= pageCount) {
                pages[i] = null
            }
        }

        const node = createLeafNode(pages)
        const { id, bytes } = await sealNode(node, sha256)
        nodes.set(id, node)
        nodeBytes.set(id, bytes)
        newLeafHashes.set(leafKey, id)
    }

    // Rebuild internal levels bottom-up, reusing unchanged children
    const i2Hashes = await rebuildInternalLevel(
        baseManifestRootId, newLeafHashes, 2, loadNode, nodes, nodeBytes, sha256,
    )
    const i1Hashes = await rebuildInternalLevel(
        baseManifestRootId, i2Hashes, 1, loadNode, nodes, nodeBytes, sha256,
    )

    // Build root
    const rootChildren: Array<string | null> = new Array(MANIFEST_V2_FANOUT).fill(null)
    for (const [key, hash] of i1Hashes) {
        const slot = Number(key)
        rootChildren[slot] = hash
    }

    // Reuse any root slots that weren't changed
    if (baseManifestRootId != null) {
        const baseRoot = await loadNode(baseManifestRootId)
        if (baseRoot?.kind === 'internal') {
            for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
                if (rootChildren[i] == null && baseRoot.children[i] != null) {
                    rootChildren[i] = baseRoot.children[i]
                }
            }
        }
    }

    const rootNode = createInternalNode(rootChildren)
    const { id: manifestRootId, bytes: rootBytes } = await sealNode(rootNode, sha256)
    nodes.set(manifestRootId, rootNode)
    nodeBytes.set(manifestRootId, rootBytes)

    return { manifestRootId, nodes, nodeBytes }
}

// ── Tree traversal (resolve pageObjectId from manifest) ────────────────

/**
 * Resolve the pageObjectId for a given 0-based page index (§8).
 *
 * Walks the manifest tree from root to leaf, returning the pageObjectId
 * at the leaf slot. Returns null if pageIndex is out of range or sparse.
 *
 * @param manifestRootId - root manifestObjectId
 * @param pageIndex - 0-based page index
 * @param loadNode - function to load a manifest node by manifestObjectId
 */
export async function resolvePageObjectId(
    manifestRootId: string | null,
    pageIndex: number,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
): Promise<string | null> {
    if (manifestRootId == null || pageIndex < 0) return null

    let currentId: string | null = manifestRootId

    // Walk internal levels (0 through MANIFEST_V2_DEPTH - 2)
    for (let level = 0; level < MANIFEST_V2_DEPTH - 1; level++) {
        if (currentId == null) return null
        const shift = (MANIFEST_V2_DEPTH - 1 - level) * 8
        const slot = (pageIndex >>> shift) & 0xFF
        const node = await loadNode(currentId)
        if (node == null || node.kind !== 'internal') return null
        currentId = node.children[slot] ?? null
    }

    // Final level is leaf
    if (currentId == null) return null
    const leafSlot = pageIndex & 0xFF
    const leaf = await loadNode(currentId)
    if (leaf == null || leaf.kind !== 'leaf') return null
    return leaf.pages[leafSlot] ?? null
}

// ── Structural diff (§10.3) ───────────────────────────────────────────

/**
 * Result of a structural diff between two manifest trees.
 *
 * Used for server-side refcount GC: removed objects get -1, added get +1.
 */
export interface ManifestDiffResult {
    /** pageObjectIds that were removed (present in old, absent in new). */
    removedPageObjectIds: Set<string>
    /** pageObjectIds that were added (absent in old, present in new). */
    addedPageObjectIds: Set<string>
    /** manifestObjectIds that were removed (old nodes no longer reachable). */
    removedManifestObjectIds: Set<string>
    /** manifestObjectIds that were added (new nodes in the tree). */
    addedManifestObjectIds: Set<string>
}

/**
 * Result of a page-level diff between two manifest trees.
 *
 * Unlike ManifestDiffResult (which tracks hash-level changes for GC),
 * this variant tracks the concrete 0-based page indices that differ,
 * making it suitable for merge-plan diffConcreteRoots without a
 * secondary O(N) per-page resolution pass.
 */
export interface ManifestPageDiffResult extends ManifestDiffResult {
    /** 0-based page indices that changed between old and new trees. */
    changedPageIndices: Set<number>
}

/**
 * Perform structural diff between two manifest roots (§10.3).
 *
 * Algorithm:
 * - If two manifestObjectIds are identical → subtree is identical, stop.
 * - If different → load both nodes, diff slot by slot, descend into changed slots.
 * - At leaf level: compare pageObjectIds per slot.
 *   - old != null && new == null → -1 (removed)
 *   - old == null && new != null → +1 (added)
 *   - old != null && new != null && old != new → -1 old, +1 new
 *
 * This is O(changed × depth) in steady-state.
 */
export async function diffManifestTrees(
    oldRootId: string | null,
    newRootId: string | null,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
): Promise<ManifestDiffResult> {
    const removedPageObjectIds = new Set<string>()
    const addedPageObjectIds = new Set<string>()
    const removedManifestObjectIds = new Set<string>()
    const addedManifestObjectIds = new Set<string>()

    async function diffSubtrees(
        oldId: string | null,
        newId: string | null,
        level: number,
    ): Promise<void> {
        // Both absent → identical
        if (oldId == null && newId == null) return

        // Identical → identical subtree (§10.3 hash-pruning)
        if (oldId === newId) return

        const isLeaf = level === MANIFEST_V2_DEPTH - 1

        if (isLeaf) {
            // Track manifest node changes
            trackManifestChange(oldId, newId, removedManifestObjectIds, addedManifestObjectIds)

            const oldLeaf = oldId != null ? await loadNode(oldId) : null
            const newLeaf = newId != null ? await loadNode(newId) : null

            const oldPages = oldLeaf?.kind === 'leaf'
                ? oldLeaf.pages
                : EMPTY_FANOUT_ARRAY
            const newPages = newLeaf?.kind === 'leaf'
                ? newLeaf.pages
                : EMPTY_FANOUT_ARRAY

            for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
                const oldPage = oldPages[i]
                const newPage = newPages[i]
                if (oldPage === newPage) continue
                if (oldPage != null) removedPageObjectIds.add(oldPage)
                if (newPage != null) addedPageObjectIds.add(newPage)
            }
        } else {
            // Track manifest node changes
            trackManifestChange(oldId, newId, removedManifestObjectIds, addedManifestObjectIds)

            const oldNode = oldId != null ? await loadNode(oldId) : null
            const newNode = newId != null ? await loadNode(newId) : null

            const oldChildren = oldNode?.kind === 'internal'
                ? oldNode.children
                : EMPTY_FANOUT_ARRAY
            const newChildren = newNode?.kind === 'internal'
                ? newNode.children
                : EMPTY_FANOUT_ARRAY

            for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
                await diffSubtrees(oldChildren[i] ?? null, newChildren[i] ?? null, level + 1)
            }
        }
    }

    await diffSubtrees(oldRootId, newRootId, 0)

    return {
        removedPageObjectIds,
        addedPageObjectIds,
        removedManifestObjectIds,
        addedManifestObjectIds,
    }
}

/**
 * Perform structural diff between two manifest roots (§10.3) **with page-index tracking**.
 *
 * Identical algorithm to diffManifestTrees but also returns the set of 0-based
 * page indices that changed. This avoids the O(N) per-page resolution pass that
 * diffConcreteRoots previously needed after calling diffManifestTrees.
 *
 * The page index is derived from the traversal path through the radix tree:
 * each level's slot contributes one byte to the final index.
 *
 * Complexity: O(changed × depth) — proportional to actual changes, not total pages.
 */
export async function diffManifestTreesWithPageIndices(
    oldRootId: string | null,
    newRootId: string | null,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
): Promise<ManifestPageDiffResult> {
    const removedPageObjectIds = new Set<string>()
    const addedPageObjectIds = new Set<string>()
    const removedManifestObjectIds = new Set<string>()
    const addedManifestObjectIds = new Set<string>()
    const changedPageIndices = new Set<number>()

    async function diffSubtrees(
        oldId: string | null,
        newId: string | null,
        level: number,
        pagePrefix: number,
    ): Promise<void> {
        // Both absent → identical
        if (oldId == null && newId == null) return

        // Identical → identical subtree (§10.3 hash-pruning)
        if (oldId === newId) return

        const isLeaf = level === MANIFEST_V2_DEPTH - 1

        if (isLeaf) {
            // Track manifest node changes
            trackManifestChange(oldId, newId, removedManifestObjectIds, addedManifestObjectIds)

            const oldLeaf = oldId != null ? await loadNode(oldId) : null
            const newLeaf = newId != null ? await loadNode(newId) : null

            const oldPages = oldLeaf?.kind === 'leaf'
                ? oldLeaf.pages
                : EMPTY_FANOUT_ARRAY
            const newPages = newLeaf?.kind === 'leaf'
                ? newLeaf.pages
                : EMPTY_FANOUT_ARRAY

            for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
                const oldPage = oldPages[i]
                const newPage = newPages[i]
                if (oldPage === newPage) continue
                if (oldPage != null) removedPageObjectIds.add(oldPage)
                if (newPage != null) addedPageObjectIds.add(newPage)
                // Track the 0-based page index from the full path through the tree
                changedPageIndices.add(pagePrefix | i)
            }
        } else {
            // Track manifest node changes
            trackManifestChange(oldId, newId, removedManifestObjectIds, addedManifestObjectIds)

            const oldNode = oldId != null ? await loadNode(oldId) : null
            const newNode = newId != null ? await loadNode(newId) : null

            const oldChildren = oldNode?.kind === 'internal'
                ? oldNode.children
                : EMPTY_FANOUT_ARRAY
            const newChildren = newNode?.kind === 'internal'
                ? newNode.children
                : EMPTY_FANOUT_ARRAY

            for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
                // Slot at level i occupies bits [(MANIFEST_V2_DEPTH-1-i)*8 .. (MANIFEST_V2_DEPTH-1-i)*8+7]
                // of the final page index. Same encoding as resolvePageObjectId uses.
                const childPrefix = pagePrefix | (i << ((MANIFEST_V2_DEPTH - 1 - level) * 8))
                await diffSubtrees(oldChildren[i] ?? null, newChildren[i] ?? null, level + 1, childPrefix)
            }
        }
    }

    await diffSubtrees(oldRootId, newRootId, 0, 0)

    return {
        removedPageObjectIds,
        addedPageObjectIds,
        removedManifestObjectIds,
        addedManifestObjectIds,
        changedPageIndices,
    }
}

// ── Validation ────────────────────────────────────────────────────────

/**
 * Validate a manifest node's structure (§7.2).
 *
 * Returns an error message if invalid, or null if valid.
 */
export function validateManifestNode(node: Sqlite3mtManifestV2Node): string | null {
    if (node.type !== 'sqlite3mt-manifest-v2') {
        return `invalid type: ${node.type}`
    }
    if (node.kind === 'internal') {
        if (node.children.length !== MANIFEST_V2_FANOUT) {
            return `internal children.length=${node.children.length}, expected ${MANIFEST_V2_FANOUT}`
        }
        return null
    }
    if (node.kind === 'leaf') {
        if (node.pages.length !== MANIFEST_V2_FANOUT) {
            return `leaf pages.length=${node.pages.length}, expected ${MANIFEST_V2_FANOUT}`
        }
        return null
    }
    return `invalid kind: ${(node as { kind: string }).kind}`
}

/**
 * Validate a sqlite3mt-public-root-v2 commit payload (§11).
 *
 * Returns an error message if invalid, or null if valid.
 */
export function validatePublicRootV2Payload(payload: Record<string, unknown>): string | null {
    if (payload.kind !== 'sqlite3mt-public-root-v2') {
        return `invalid kind: ${payload.kind}`
    }
    if (typeof payload.rootId !== 'string' || !payload.rootId) {
        return 'missing or invalid rootId'
    }
    if (typeof payload.pageCount !== 'string' || !isUnsignedDecimalString(payload.pageCount)) {
        return `invalid pageCount: ${payload.pageCount}`
    }
    if (typeof payload.pageSize !== 'string' || !isUnsignedDecimalString(payload.pageSize)) {
        return `invalid pageSize: ${payload.pageSize}`
    }
    if (typeof payload.version !== 'string' || !isUnsignedDecimalString(payload.version)) {
        return `invalid version: ${payload.version}`
    }
    if (payload.manifestRootId != null && typeof payload.manifestRootId !== 'string') {
        return `invalid manifestRootId: ${payload.manifestRootId}`
    }
    if (payload.parentPublicRootId != null && typeof payload.parentPublicRootId !== 'string') {
        return `invalid parentPublicRootId: ${payload.parentPublicRootId}`
    }

    const pageCount = Number(payload.pageCount)
    if (pageCount === 0 && payload.manifestRootId != null) {
        return 'manifestRootId must be null when pageCount is 0'
    }
    if (pageCount > 0 && payload.manifestRootId == null) {
        return 'manifestRootId must not be null when pageCount > 0'
    }

    return null
}

// ── Internal helpers ──────────────────────────────────────────────────

/** Cached all-null array for diff defaults. */
const EMPTY_FANOUT_ARRAY: ReadonlyArray<string | null> = Object.freeze(
    new Array<string | null>(MANIFEST_V2_FANOUT).fill(null),
)

/**
 * Compute the path prefix key for a given page index at a given level.
 *
 * For level 3 (leaf grouping): returns "b3:b2:b1"
 * For level 2: returns "b3:b2"
 * For level 1: returns "b3"
 */
function manifestPathPrefix(pageIndex: number, level: number): string {
    const b3 = (pageIndex >>> 24) & 0xFF
    const b2 = (pageIndex >>> 16) & 0xFF
    const b1 = (pageIndex >>> 8) & 0xFF

    switch (level) {
        case 3: return `${b3}:${b2}:${b1}`
        case 2: return `${b3}:${b2}`
        case 1: return `${b3}`
        default: throw new Error(`invalid manifest path level: ${level}`)
    }
}

/**
 * Resolve the child manifestObjectId at a given path prefix from the root.
 *
 * For leafKey "b3:b2:b1": walks root.children[b3].children[b2].children[b1]
 */
async function resolveChildAtPath(
    rootId: string,
    pathKey: string,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
): Promise<string | null> {
    const parts = pathKey.split(':').map(Number)
    let currentId: string | null = rootId

    for (const slot of parts) {
        if (currentId == null) return null
        const node = await loadNode(currentId)
        if (node == null || node.kind !== 'internal') return null
        currentId = node.children[slot] ?? null
    }

    return currentId
}

/**
 * Build one level of internal nodes from child hashes.
 *
 * childHashes: Map<pathKey, manifestObjectId> for the level below.
 * level: the internal level being built (1 or 2).
 */
async function buildInternalLevel(
    childHashes: Map<string, string>,
    level: number,
    nodes: Map<string, Sqlite3mtManifestV2Node>,
    nodeBytes: Map<string, Uint8Array>,
    sha256: (data: Uint8Array) => Promise<string>,
): Promise<Map<string, string>> {
    // Group children by their prefix at this level
    const groups = new Map<string, Map<number, string>>()
    for (const [childKey, hash] of childHashes) {
        const parentKey = manifestPathPrefixFromChild(childKey, level)
        let group = groups.get(parentKey)
        if (!group) {
            group = new Map()
            groups.set(parentKey, group)
        }
        // The slot at this level is the byte at position `level`
        const slot = slotFromPathKey(childKey, level)
        group.set(slot, hash)
    }

    const result = new Map<string, string>()
    for (const [key, children] of groups) {
        const childArray: Array<string | null> = new Array(MANIFEST_V2_FANOUT).fill(null)
        for (const [slot, hash] of children) {
            childArray[slot] = hash
        }
        const node = createInternalNode(childArray)
        const { id, bytes } = await sealNode(node, sha256)
        nodes.set(id, node)
        nodeBytes.set(id, bytes)
        result.set(key, id)
    }

    return result
}

/**
 * Rebuild one level of internal nodes, reusing unchanged children from base.
 */
async function rebuildInternalLevel(
    baseRootId: string | null,
    changedChildHashes: Map<string, string>,
    level: number,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
    nodes: Map<string, Sqlite3mtManifestV2Node>,
    nodeBytes: Map<string, Uint8Array>,
    sha256: (data: Uint8Array) => Promise<string>,
): Promise<Map<string, string>> {
    // Group changed children by parent at this level
    const groups = new Map<string, Map<number, string>>()
    for (const [childKey, hash] of changedChildHashes) {
        const parentKey = manifestPathPrefixFromChild(childKey, level)
        let group = groups.get(parentKey)
        if (!group) {
            group = new Map()
            groups.set(parentKey, group)
        }
        const slot = slotFromPathKey(childKey, level)
        group.set(slot, hash)
    }

    const result = new Map<string, string>()
    for (const [parentKey, changedChildren] of groups) {
        const childArray: Array<string | null> = new Array(MANIFEST_V2_FANOUT).fill(null)

        // Load base children for this internal node
        if (baseRootId != null) {
            const baseChildId = await resolveChildAtLevel(baseRootId, parentKey, level, loadNode)
            if (baseChildId != null) {
                const baseNode = await loadNode(baseChildId)
                if (baseNode?.kind === 'internal') {
                    for (let i = 0; i < MANIFEST_V2_FANOUT; i++) {
                        childArray[i] = baseNode.children[i]
                    }
                }
            }
        }

        // Apply changes
        for (const [slot, hash] of changedChildren) {
            childArray[slot] = hash
        }

        const node = createInternalNode(childArray)
        const { id, bytes } = await sealNode(node, sha256)
        nodes.set(id, node)
        nodeBytes.set(id, bytes)
        result.set(parentKey, id)
    }

    return result
}

/**
 * Resolve the manifestObjectId at a parent path from the root.
 */
async function resolveChildAtLevel(
    rootId: string,
    parentKey: string,
    targetLevel: number,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
): Promise<string | null> {
    const slots = parentKey.split(':').map(Number)
    let currentId: string | null = rootId

    for (const slot of slots) {
        if (currentId == null) return null
        const node = await loadNode(currentId)
        if (node == null || node.kind !== 'internal') return null
        currentId = node.children[slot] ?? null
    }

    return currentId
}

/**
 * Derive the parent path key from a child path key.
 *
 * Level 3 key "b3:b2:b1" → Level 2 parent "b3:b2"
 * Level 2 key "b3:b2"    → Level 1 parent "b3"
 * Level 1 key "b3"       → Level 0 (root) parent ""
 */
function manifestPathPrefixFromChild(childKey: string, parentLevel: number): string {
    const parts = childKey.split(':')
    if (parentLevel >= parts.length) {
        // Root level
        return ''
    }
    return parts.slice(0, parts.length - 1).join(':')
}

/**
 * Extract the slot number at a given level from a path key.
 */
function slotFromPathKey(pathKey: string, level: number): number {
    const parts = pathKey.split(':').map(Number)
    // The child key has (targetLevel+1) components.
    // The slot at parentLevel is the component at index parentLevel.
    // For level 3 key "b3:b2:b1" → slot at level 2 is b1
    // For level 2 key "b3:b2" → slot at level 1 is b2
    // For level 1 key "b3" → slot at level 0 is b3
    return parts[parts.length - 1]
}

/** Seal a node: canonicalize, hash, return both. */
async function sealNode(
    node: Sqlite3mtManifestV2Node,
    sha256: (data: Uint8Array) => Promise<string>,
): Promise<{ id: string; bytes: Uint8Array }> {
    const bytes = encodeManifestNode(node)
    const id = await sha256(bytes)
    return { id, bytes }
}

function trackManifestChange(
    oldId: string | null,
    newId: string | null,
    removed: Set<string>,
    added: Set<string>,
): void {
    if (oldId != null && newId == null) removed.add(oldId)
    else if (newId != null && oldId == null) added.add(newId)
    else if (oldId != null && newId != null && oldId !== newId) {
        removed.add(oldId)
        added.add(newId)
    }
}

function padArray<T>(arr: ReadonlyArray<T>, length: number, fill: T): T[] {
    if (arr.length === length) return [...arr]
    const result = new Array<T>(length).fill(fill)
    for (let i = 0; i < Math.min(arr.length, length); i++) {
        result[i] = arr[i]
    }
    return result
}

function encodeVersion(version: bigint | number | string): string {
    if (typeof version === 'bigint') {
        if (version < 0n) throw new Error('version must be non-negative')
        return version.toString()
    }
    if (typeof version === 'number') {
        if (!Number.isSafeInteger(version) || version < 0) {
            throw new Error('version must be non-negative safe integer')
        }
        return version.toString()
    }
    if (!isUnsignedDecimalString(version)) {
        throw new Error('version must be unsigned decimal string')
    }
    return version
}

function encodeUnsignedDecimal(value: number, name: string): string {
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new Error(`${name} must be non-negative safe integer, got ${value}`)
    }
    return value.toString()
}

function isUnsignedDecimalString(s: unknown): boolean {
    return typeof s === 'string' && /^(0|[1-9][0-9]*)$/.test(s)
}

function parseUnsignedDecimalToSafeInteger(value: string, name: string): number {
    const parsed = Number(value)
    if (!Number.isSafeInteger(parsed) || parsed < 0) {
        throw new Error(`${name} must be a non-negative safe integer, got ${value}`)
    }
    return parsed
}

function hexToUint8Array(hex: string): Uint8Array {
    if (hex.length % 2 !== 0) throw new Error('hex string must have even length')
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
    }
    return bytes
}
